import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { getPractitionerDisplayName } from '../utils/practitionerDisplayName';
import { hasVisibleHtmlContent } from '../utils/htmlContent';
import { filterPastSlotsForToday, formatLocalDate } from '../utils/slotFilters';
import { 
  ArrowLeft, 
  ArrowRight, 
  MapPin, 
  User, 
  Palette,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Shield,
  Calendar,
  Camera,
  Upload,
  X,
  CreditCard,
  MessageCircle,
  Info
} from 'lucide-react';
import { onlineBookingApi, Shop, Service, Practitioner, Slot, ReferenceImage, OnlineBookingRequest } from '../services/onlineBookingApi';
import { CustomerDetailsStep, ClientData } from '../components/CustomerDetailsStep';
import { BookingWizardContext, BookingContextChip } from '../components/BookingWizardContext';
import { BookingStepper } from '../components/BookingStepper';
import { LandscapeHint } from '../components/LandscapeHint';
import { RichTextEditor } from '../components/RichTextEditor';
import { DateInput } from '../components/DateInput';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useScrollOnStepChange } from '../hooks/useScrollOnStepChange';
import {
  ENABLED_ONLINE_BOOKING_PAYMENT_METHOD_OPTIONS,
  type OnlineBookingPaymentMethod,
} from '../constants/paymentMethods';
import {
  buildTattooBookingSteps,
  normalizeWizardStep,
  type TattooBookingAuthPlacement,
  type TattooWizardStep,
} from '../utils/tattooBookingSteps';
import { calculateServiceDepositAmount } from '@digitaltattoo/shared-utils';

type WizardStep = TattooWizardStep;

interface PendingReferenceImage {
  localId: string;
  file: File;
  previewUrl: string;
  fileName: string;
}

interface BookingFormData {
  shopId: string;
  serviceId: string;
  practitionerId?: string;
  appointmentDate: string;
  selectedTimeSlot: string;
  clientId?: string;
  preferredLanguage?: 'fr' | 'en';
  project: {
    description: string;
    motif?: string;
    placement: {
      area: string;
      side?: string;
    };
    size: {
      width: number;
      height: number;
      unit: 'cm' | 'inch';
    };
    style: 'TRADITIONAL' | 'REALISM' | 'WATERCOLOR' | 'GEOMETRIC' | 'MINIMALIST' | 'BLACKWORK' | 'DOTWORK' | 'TRIBAL' | 'JAPANESE' | 'BIOMECHANICAL';
    colorType: 'BLACK_GREY' | 'COLOR' | 'MIXED';
    notesToArtist?: string;
  };
  referenceImages: ReferenceImage[];
  termsAccepted: boolean;
  cancellationPolicyAccepted: boolean;
  imageRightsAccepted: boolean;
  paymentMethod: OnlineBookingPaymentMethod;
}

// Auth step is inserted dynamically from SystemSettings.tattooBookingAuthPlacement.
// Project step includes optional reference images (merged former "images" step).
const DEFAULT_AUTH_PLACEMENT: TattooBookingAuthPlacement = 'before_summary';
const TATTOO_BOOKING_RESUME_KEY = 'tattooBookingResume';
const TATTOO_CADDY_STORAGE_KEY = 'tattooBookingCaddy';

interface PersistedTattooDraft {
  currentStep: WizardStep;
  formData: Partial<BookingFormData>;
}

function getDefaultFormData(): BookingFormData {
  return {
    shopId: '',
    serviceId: '',
    practitionerId: undefined,
    appointmentDate: '',
    selectedTimeSlot: '',
    project: {
      description: '',
      motif: '',
      placement: { area: '', side: '' },
      size: { width: 0, height: 0, unit: 'cm' },
      style: 'TRADITIONAL',
      colorType: 'BLACK_GREY',
      notesToArtist: ''
    },
    referenceImages: [],
    termsAccepted: false,
    cancellationPolicyAccepted: false,
    imageRightsAccepted: false,
    paymentMethod: 'CARD'
  };
}

function isFreshBookingStart(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('fresh') === '1';
}

function readPersistedTattooDraft(
  authPlacement: TattooBookingAuthPlacement = DEFAULT_AUTH_PLACEMENT,
): PersistedTattooDraft | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = sessionStorage.getItem(TATTOO_BOOKING_RESUME_KEY);
    const fallback = localStorage.getItem(TATTOO_CADDY_STORAGE_KEY);
    const raw = stored || fallback;
    if (!raw) return null;

    const parsed = JSON.parse(raw) as PersistedTattooDraft;
    if (parsed?.currentStep === 'success') {
      sessionStorage.removeItem(TATTOO_BOOKING_RESUME_KEY);
      localStorage.removeItem(TATTOO_CADDY_STORAGE_KEY);
      return null;
    }

    const defaultSteps = buildTattooBookingSteps(false, authPlacement);
    const fallbackStep = defaultSteps[0] ?? 'shop';

    return {
      currentStep: normalizeWizardStep(
        parsed?.currentStep && typeof parsed.currentStep === 'string' ? parsed.currentStep : undefined,
        defaultSteps,
        fallbackStep,
      ),
      formData: parsed?.formData && typeof parsed.formData === 'object' ? parsed.formData : {},
    };
  } catch (error) {
    console.warn('Failed to read tattoo booking session:', error);
    return null;
  }
}

export function OnlineBookingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { isTestMode, tattooBookingAuthPlacement } = useSystemStatus();
  
  const isFreshStart = searchParams.get('fresh') === '1';
  const initialSteps = buildTattooBookingSteps(false, DEFAULT_AUTH_PLACEMENT);
  
  const [currentStep, setCurrentStep] = useState<WizardStep>(() => {
    if (isFreshBookingStart()) return initialSteps[0];
    const draft = readPersistedTattooDraft(DEFAULT_AUTH_PLACEMENT);
    if (draft?.currentStep) return draft.currentStep;
    const urlStep = new URLSearchParams(window.location.search).get('step');
    return normalizeWizardStep(urlStep ?? undefined, initialSteps, initialSteps[0]);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data state
  const [shops, setShops] = useState<Shop[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const servicesRequestIdRef = useRef(0);
  const practitionersRequestIdRef = useRef(0);
  
  // Form data: restore from storage on refresh or when returning from login
  const [formData, setFormData] = useState<BookingFormData>(() => ({
    ...getDefaultFormData(),
    ...(isFreshBookingStart() ? {} : readPersistedTattooDraft()?.formData ?? {}),
  }));
  useEffect(() => {
    if (
      !ENABLED_ONLINE_BOOKING_PAYMENT_METHOD_OPTIONS.some((method) => method.value === formData.paymentMethod) &&
      ENABLED_ONLINE_BOOKING_PAYMENT_METHOD_OPTIONS[0]
    ) {
      setFormData(prev => ({ ...prev, paymentMethod: ENABLED_ONLINE_BOOKING_PAYMENT_METHOD_OPTIONS[0].value }));
    }
  }, [formData.paymentMethod]);
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImages, setPendingImages] = useState<PendingReferenceImage[]>([]);
  const [bookingResult, setBookingResult] = useState<any>(null);
  const resumeStateRef = useRef({ currentStep, formData });
  const bookingTopRef = useRef<HTMLDivElement>(null);
  const pendingImagesRef = useRef<PendingReferenceImage[]>([]);
  
  const isConsultation = selectedService?.category === 'CONSULTATION';

  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, []);

  // Prefill clientId when already logged in
  useEffect(() => {
    if (isAuthenticated && user?.id && !formData.clientId) {
      setFormData((prev) => ({ ...prev, clientId: user.id }));
    }
  }, [isAuthenticated, user?.id, formData.clientId]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    let changed = false;

    if (isFreshStart) {
      sessionStorage.removeItem(TATTOO_BOOKING_RESUME_KEY);
      localStorage.removeItem(TATTOO_CADDY_STORAGE_KEY);
      nextParams.delete('fresh');
      changed = true;
    }

    // step=customer is only used when returning from login; strip it so refresh uses persisted progress.
    if (nextParams.has('step')) {
      nextParams.delete('step');
      changed = true;
    }

    if (changed) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [isFreshStart, searchParams, setSearchParams]);

  useEffect(() => {
    resumeStateRef.current = { currentStep, formData };
  }, [currentStep, formData]);

  const saveBookingDraft = () => {
    const resumeData = resumeStateRef.current;
    if (resumeData.currentStep === 'success') return;
    try {
      const payload = JSON.stringify({
        currentStep: resumeData.currentStep,
        formData: resumeData.formData,
      });
      sessionStorage.setItem(TATTOO_BOOKING_RESUME_KEY, payload);
      localStorage.setItem(TATTOO_CADDY_STORAGE_KEY, payload);
    } catch (error) {
      console.warn('Failed to save tattoo booking session:', error);
    }
  };

  useEffect(() => {
    saveBookingDraft();
  }, [currentStep, formData]);

  useEffect(() => {
    if (currentStep === 'success') {
      sessionStorage.removeItem(TATTOO_BOOKING_RESUME_KEY);
      localStorage.removeItem(TATTOO_CADDY_STORAGE_KEY);
    }
  }, [currentStep]);

  const activeSteps = useMemo<WizardStep[]>(() => {
    return buildTattooBookingSteps(isConsultation, tattooBookingAuthPlacement);
  }, [isConsultation, tattooBookingAuthPlacement]);

  // Keep current step valid when consultation mode or auth placement changes
  useEffect(() => {
    if (currentStep === 'payment' || currentStep === 'success') return;
    if (!activeSteps.includes(currentStep)) {
      const normalized = normalizeWizardStep(currentStep, activeSteps, activeSteps[0]);
      setCurrentStep(normalized);
    }
  }, [activeSteps, currentStep]);

  const stepConfig: Record<string, { title: string; icon: React.ReactNode }> = {
    customer: { title: t('onlineBooking.steps.customer'), icon: <User className="w-5 h-5" /> },
    shop: { title: t('onlineBooking.steps.shop'), icon: <MapPin className="w-5 h-5" /> },
    service: { title: t('onlineBooking.steps.service'), icon: <Shield className="w-5 h-5" /> },
    practitioner: { title: t('onlineBooking.steps.practitioner'), icon: <User className="w-5 h-5" /> },
    project: { title: t('onlineBooking.steps.project'), icon: <Palette className="w-5 h-5" /> },
    slot: { title: t('onlineBooking.steps.slot'), icon: <Clock className="w-5 h-5" /> },
    summary: { title: t('onlineBooking.steps.summary'), icon: <CheckCircle className="w-5 h-5" /> },
  };

  const steps = activeSteps.map(key => ({ key, label: stepConfig[key].title, icon: stepConfig[key].icon, title: stepConfig[key].title }));
  
  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  useScrollOnStepChange(currentStep, bookingTopRef);

  const bookingContextItems = useMemo((): BookingContextChip[] => {
    const stepIdx = activeSteps.indexOf(currentStep);
    const items: BookingContextChip[] = [];

    const shopStepIdx = activeSteps.indexOf('shop');
    const practitionerStepIdx = activeSteps.indexOf('practitioner');
    const projectStepIdx = activeSteps.indexOf('project');
    const slotStepIdx = activeSteps.indexOf('slot');

    if (selectedShop && stepIdx > shopStepIdx) {
      items.push({
        key: 'shop',
        icon: <MapPin className="w-3.5 h-3.5" />,
        label: selectedShop.name,
      });
    }

    if (selectedService && stepIdx > shopStepIdx) {
      items.push({
        key: 'service',
        icon: <Shield className="w-3.5 h-3.5" />,
        label: selectedService.name,
      });
    }

    if (selectedPractitioner && stepIdx > practitionerStepIdx) {
      items.push({
        key: 'practitioner',
        icon: <User className="w-3.5 h-3.5" />,
        label: getPractitionerDisplayName(selectedPractitioner),
      });
    }

    if (!isConsultation && formData.project.placement.area && stepIdx > projectStepIdx) {
      items.push({
        key: 'placement',
        icon: <Palette className="w-3.5 h-3.5" />,
        label: formData.project.placement.area,
      });
    }

    if (formData.appointmentDate && formData.selectedTimeSlot && stepIdx > slotStepIdx) {
      const dateLabel = new Date(formData.appointmentDate).toLocaleDateString('fr-FR');
      items.push({
        key: 'slot',
        icon: <Clock className="w-3.5 h-3.5" />,
        label: `${dateLabel} — ${formData.selectedTimeSlot}`,
      });
    }

    return items;
  }, [
    activeSteps,
    currentStep,
    selectedShop,
    selectedService,
    selectedPractitioner,
    formData.project.placement.area,
    formData.appointmentDate,
    formData.selectedTimeSlot,
    isConsultation,
  ]);

  const goToNextStep = () => {
    const idx = activeSteps.indexOf(currentStep);
    if (idx >= 0 && idx < activeSteps.length - 1) {
      setError(null);
      setCurrentStep(activeSteps[idx + 1]);
    }
  };

  const goToPrevStep = () => {
    const idx = activeSteps.indexOf(currentStep);
    if (idx > 0) {
      setError(null);
      setCurrentStep(activeSteps[idx - 1]);
    }
  };
  
  useEffect(() => {
    loadInitialData();
  }, []);

  // When returning from login with restored formData, set selected shop/service/practitioner once data is loaded
  useEffect(() => {
    if (shops.length > 0 && formData.shopId && !selectedShop) {
      setSelectedShop(shops.find(s => s.id === formData.shopId) ?? null);
    }
  }, [shops, formData.shopId, selectedShop]);
  useEffect(() => {
    if (services.length > 0 && formData.serviceId && !selectedService) {
      setSelectedService(services.find(s => s.id === formData.serviceId) ?? null);
    }
  }, [services, formData.serviceId, selectedService]);
  useEffect(() => {
    if (practitioners.length > 0 && formData.practitionerId && !selectedPractitioner) {
      setSelectedPractitioner(practitioners.find(p => p.id === formData.practitionerId) ?? null);
    }
  }, [practitioners, formData.practitionerId, selectedPractitioner]);

  // Show a waiting cursor while loading available slots
  useEffect(() => {
    if (loadingSlots) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loadingSlots]);
  
  useEffect(() => {
    if (!formData.shopId) {
      setServices([]);
      setLoadingServices(false);
      return;
    }
    setServices([]);
    setError(null);
    void loadServices(formData.shopId);
  }, [formData.shopId]);

  useEffect(() => {
    if (!formData.shopId) {
      setPractitioners([]);
      return;
    }
    void loadPractitioners(formData.shopId, formData.serviceId || undefined);
  }, [formData.shopId, formData.serviceId]);
  
  useEffect(() => {
    if (currentStep !== 'slot') return;
    if (formData.shopId && formData.serviceId && formData.appointmentDate && formData.practitionerId) {
      loadAvailability();
    } else {
      setAvailableSlots([]);
      setSlotsLoaded(false);
    }
  }, [currentStep, formData.shopId, formData.serviceId, formData.appointmentDate, formData.practitionerId]);
  
  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await onlineBookingApi.getShops();
      if (response.success) {
        setShops(response.data.shops || []);
        if (!response.data.shops || response.data.shops.length === 0) {
          setError(t('onlineBooking.shop.noShops'));
        }
      } else {
        setError(t('onlineBooking.errors.loadShops'));
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || t('onlineBooking.errors.loadShops');
      setError(errorMessage);
      console.error('Error loading shops:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadServices = async (shopId: string) => {
    const requestId = ++servicesRequestIdRef.current;
    setLoadingServices(true);
    try {
      const response = await onlineBookingApi.getServices(shopId, 'TATTOO,CONSULTATION');
      // Ignore stale responses from a previous shop selection
      if (requestId !== servicesRequestIdRef.current) return;

      if (response.success) {
        const loadedServices = response.data.services || [];
        setServices(loadedServices);

        // Drop a selected service that does not belong to this shop
        setFormData((prev) => {
          if (prev.shopId !== shopId) return prev;
          if (prev.serviceId && !loadedServices.some((s) => s.id === prev.serviceId)) {
            return { ...prev, serviceId: '', practitionerId: undefined };
          }
          return prev;
        });
        setSelectedService((prev) =>
          prev && loadedServices.some((s) => s.id === prev.id) ? prev : null,
        );

        if (loadedServices.length === 0) {
          setError(t('onlineBooking.service.noServices'));
        } else {
          setError(null);
        }
      } else {
        setError(t('onlineBooking.errors.loadServices'));
      }
    } catch (err: any) {
      if (requestId !== servicesRequestIdRef.current) return;
      const errorMessage = err?.data?.message || err?.message || t('onlineBooking.errors.loadServices');
      setError(errorMessage);
      console.error('Failed to load services:', err);
    } finally {
      if (requestId === servicesRequestIdRef.current) {
        setLoadingServices(false);
      }
    }
  };
  
  const loadPractitioners = async (shopId: string, serviceId?: string) => {
    const requestId = ++practitionersRequestIdRef.current;
    try {
      const response = await onlineBookingApi.getPractitioners(shopId, serviceId);
      if (requestId !== practitionersRequestIdRef.current) return;

      if (response.success) {
        const loaded = response.data.practitioners;
        setPractitioners(loaded);
        setFormData((prev) => {
          if (prev.shopId !== shopId) return prev;
          if (prev.practitionerId && !loaded.some((p) => p.id === prev.practitionerId)) {
            return { ...prev, practitionerId: undefined };
          }
          return prev;
        });
        setSelectedPractitioner((prev) =>
          prev && loaded.some((p) => p.id === prev.id) ? prev : null,
        );
      }
    } catch (err) {
      if (requestId !== practitionersRequestIdRef.current) return;
      console.error('Failed to load practitioners:', err);
    }
  };
  
  const loadAvailability = async (overrideDate?: string) => {
    const shopId = formData.shopId;
    const serviceId = formData.serviceId;
    const appointmentDate = overrideDate ?? formData.appointmentDate;
    if (!shopId || !serviceId || !appointmentDate || !formData.practitionerId) return;
    
    try {
      setError(null);
      setLoadingSlots(true);
      setSlotsLoaded(false);
      const today = new Date();
      const fromDate = appointmentDate || today.toISOString().split('T')[0];
      const toDate = new Date(fromDate);
      toDate.setDate(toDate.getDate() + 7); // Next 7 days
      
      const from = new Date(`${fromDate}T00:00:00`).toISOString();
      const to = toDate.toISOString();
      
      const response = await onlineBookingApi.getAvailability(
        shopId,
        serviceId,
        from,
        to,
        formData.practitionerId
      );
      
      if (response.success) {
        setAvailableSlots(response.data.slots);
        setDateRange({ from, to });
        setSlotsLoaded(true);
      } else {
        setError(t('onlineBooking.errors.loadAvailability'));
      }
    } catch (err: any) {
      console.error('Failed to load availability:', err);
      // Extract error message from API response
      let errorMessage = t('onlineBooking.errors.loadAvailability');
      if (err?.data) {
        if (typeof err.data === 'object' && err.data.message) {
          errorMessage = err.data.message;
        } else if (typeof err.data === 'string') {
          errorMessage = err.data;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoadingSlots(false);
    }
  };
  
  const handleImageUpload = async (file: File) => {
    const totalImages = formData.referenceImages.length + pendingImages.length;
    if (totalImages >= 5) {
      setError(t('onlineBooking.images.maxImages'));
      return;
    }

    const token = localStorage.getItem('client_token');
    const canUploadNow = isAuthenticated || !!token;

    // Defer upload until authenticated (auth often comes later in the flow)
    if (!canUploadNow) {
      const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const previewUrl = URL.createObjectURL(file);
      setPendingImages((prev) => [
        ...prev,
        { localId, file, previewUrl, fileName: file.name },
      ]);
      setError(null);
      return;
    }

    setUploadingImage(true);
    try {
      const response = await onlineBookingApi.uploadImage(
        file,
        true, // rightsConfirmed
        'REFERENCE_ONLY', // usageRights
        true // gdprConsentGiven
      );

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          referenceImages: [...prev.referenceImages, response.data]
        }));
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || t('onlineBooking.images.uploadFailed'));
    } finally {
      setUploadingImage(false);
    }
  };

  const flushPendingImages = async (): Promise<ReferenceImage[]> => {
    const pending = pendingImagesRef.current;
    const existingImages = resumeStateRef.current.formData.referenceImages ?? [];
    if (pending.length === 0) return existingImages;

    const token = localStorage.getItem('client_token');
    if (!isAuthenticated && !token) {
      throw new Error(t('onlineBooking.images.authRequired'));
    }

    setUploadingImage(true);
    try {
      const uploaded: ReferenceImage[] = [];
      for (const item of pending) {
        const response = await onlineBookingApi.uploadImage(
          item.file,
          true,
          'REFERENCE_ONLY',
          true,
        );
        if (response.success) {
          uploaded.push(response.data);
        }
      }
      pending.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      setPendingImages([]);
      const nextImages = [...existingImages, ...uploaded];
      setFormData((prev) => ({ ...prev, referenceImages: nextImages }));
      return nextImages;
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleRemoveImage = (imageId: string) => {
    setFormData(prev => ({
      ...prev,
      referenceImages: prev.referenceImages.filter(img => img.imageId !== imageId)
    }));
  };

  const handleRemovePendingImage = (localId: string) => {
    setPendingImages((prev) => {
      const target = prev.find((img) => img.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((img) => img.localId !== localId);
    });
  };
  
  const handleCreateBooking = async () => {
    const needImageRights = !isConsultation;
    if (!formData.termsAccepted || !formData.cancellationPolicyAccepted || (needImageRights && !formData.imageRightsAccepted)) {
      setError(t('onlineBooking.errors.acceptTerms'));
      return;
    }

    // Booking endpoint requires client authentication (JWT).
    // Fail fast with a clear UX instead of doing a request that will 401.
    const token = localStorage.getItem('client_token');
    if (!isAuthenticated && !token) {
      setError(t('onlineBooking.images.authRequired'));
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let referenceImages = formData.referenceImages;
      if (!isConsultation && pendingImagesRef.current.length > 0) {
        referenceImages = await flushPendingImages();
      }

      const clientId = formData.clientId;
      
      if (!clientId) {
        setError(t('onlineBooking.errors.clientRequired'));
        setLoading(false);
        return;
      }
      
      const bookingRequest: OnlineBookingRequest = {
        clientId,
        shopId: formData.shopId,
        serviceId: formData.serviceId,
        practitionerId: formData.practitionerId,
        appointmentDate: formData.appointmentDate,
        selectedTimeSlot: formData.selectedTimeSlot,
        ...(isConsultation ? {} : {
          project: formData.project,
          referenceImageIds: referenceImages.map(img => img.imageId),
          referenceImages: referenceImages.map(img => ({
            imageId: img.imageId,
            fileName: img.fileName,
            url: img.url,
            thumbnailUrl: img.thumbnailUrl,
            fileSize: img.fileSize,
            mimeType: img.mimeType,
            tempStorage: img.tempStorage,
          })),
        }),
        paymentMethod: formData.paymentMethod,
        termsAccepted: formData.termsAccepted,
        cancellationPolicyAccepted: formData.cancellationPolicyAccepted,
        imageRightsAccepted: isConsultation ? true : formData.imageRightsAccepted
      };
      
      const response = await onlineBookingApi.createBooking(bookingRequest);
      
      if (response.success) {
        setBookingResult(response.data);
        sessionStorage.removeItem(TATTOO_BOOKING_RESUME_KEY);
        localStorage.removeItem(TATTOO_CADDY_STORAGE_KEY);
        if (response.data.status === 'PENDING_PAYMENT') {
          setCurrentStep('payment');
        } else {
          setCurrentStep('success');
        }
      }
    } catch (err: any) {
      setError(err.data?.message || err.message || t('onlineBooking.errors.createBooking'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleDepositPayment = async () => {
    if (!bookingResult) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Create a Stripe Checkout Session and redirect
      const response = await onlineBookingApi.createCheckoutSession(bookingResult.appointmentId);
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return;
      }
    } catch (err: any) {
      setError(err.data?.message || err.message || t('onlineBooking.errors.paymentFailed'));
    } finally {
      setLoading(false);
    }
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 'shop':
        // Shop step also collects the service
        return !!formData.shopId && !!formData.serviceId;
      case 'service':
        return !!formData.serviceId;
      case 'practitioner':
        return !!formData.practitionerId;
      case 'project':
        return !!formData.project.description && !!formData.project.placement.area && formData.project.size.width > 0 && formData.project.size.height > 0;
      case 'customer':
        // CustomerDetailsStep handles its own progression via onComplete callback
        return !!formData.clientId;
      case 'slot': {
        if (!formData.appointmentDate || !formData.selectedTimeSlot) return false;
        const selectedSlot = availableSlots.find(slot => {
          const slotDate = new Date(slot.start).toISOString().split('T')[0];
          const time24h = new Date(slot.start).toTimeString().slice(0, 5);
          return slotDate === formData.appointmentDate && time24h === formData.selectedTimeSlot;
        });
        return !!selectedSlot?.isAvailable;
      }
      case 'summary':
        if (isConsultation) {
          return formData.termsAccepted && formData.cancellationPolicyAccepted;
        }
        return formData.termsAccepted && formData.cancellationPolicyAccepted && formData.imageRightsAccepted;
      default:
        return false;
    }
  };
  
  const handleNext = () => {
    if (!canProceed()) {
      setError(t('onlineBooking.errors.completeFields'));
      return;
    }
    goToNextStep();
  };

  const advanceAfterSingleSelection = () => {
    setError(null);
    goToNextStep();
  };

  const handleShopSelect = (shop: Shop) => {
    setFormData(prev => ({
      ...prev,
      shopId: shop.id,
      serviceId: shop.id === prev.shopId ? prev.serviceId : '',
      practitionerId: shop.id === prev.shopId ? prev.practitionerId : undefined,
    }));
    setSelectedShop(shop);
    if (shop.id !== formData.shopId) {
      setSelectedService(null);
      setSelectedPractitioner(null);
    }
  };

  const handleServiceSelect = (service: Service) => {
    setFormData(prev => ({ ...prev, serviceId: service.id }));
    setSelectedService(service);
    // Only auto-advance when both shop and service are chosen and there is a single service option
    if (formData.shopId && services.length === 1) {
      advanceAfterSingleSelection();
    }
  };

  const handlePractitionerSelect = (practitioner: Practitioner) => {
    setFormData(prev => ({ ...prev, practitionerId: practitioner.id }));
    setSelectedPractitioner(practitioner);
    if (practitioners.length === 1) {
      advanceAfterSingleSelection();
    }
  };

  const handleTimeSlotSelect = (time24h: string, availableSlotCount: number) => {
    setFormData(prev => ({ ...prev, selectedTimeSlot: time24h }));
    if (availableSlotCount === 1) {
      advanceAfterSingleSelection();
    }
  };
  
  const handleBack = () => {
    setError(null);
    goToPrevStep();
  };
  
  const selectionCardClass = (selected: boolean) =>
    `p-3 sm:p-3.5 border rounded-lg cursor-pointer transition-colors ${
      selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
    }`;

  const renderShopSelection = () => {
    const consultationServices = services.filter(s => s.category === 'CONSULTATION');
    const tattooServices = services.filter(s => s.category !== 'CONSULTATION');
    const hasConsultation = consultationServices.length > 0;

    return (
    <div className="space-y-4 sm:space-y-5">
      <div className="space-y-2.5">
        <h2 className="text-xl sm:text-2xl font-bold leading-tight">{t('onlineBooking.shop.title')}</h2>
        <p className="text-xs uppercase tracking-wide text-textSecondary">{t('onlineBooking.shop.shopSection')}</p>
        {shops.length === 0 ? (
          <div className="text-center py-6">
            <AlertTriangle className="w-10 h-10 text-orange-500 mx-auto mb-3" />
            <p className="text-gray-600 mb-1 text-sm">{t('onlineBooking.shop.noShops')}</p>
            <p className="text-xs text-gray-500">
              {t('onlineBooking.shop.contactMessage')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {shops.map(shop => (
              <div
                key={shop.id}
                onClick={() => handleShopSelect(shop)}
                className={selectionCardClass(formData.shopId === shop.id)}
              >
                <h3 className="font-semibold text-textPrimary text-sm sm:text-base leading-snug">{shop.name}</h3>
                <p className="text-xs sm:text-sm text-textSecondary mt-0.5 line-clamp-2">
                  {typeof shop.address === 'object' ? `${shop.address.street || ''}, ${shop.address.city || ''}` : shop.address}
                </p>
                {shop.phone && (
                  <p className="text-xs text-textSecondary mt-0.5 hidden sm:block">{shop.phone}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {formData.shopId && (
        <div className="space-y-2.5 border-t border-gray-200 pt-3 sm:pt-4">
          <p className="text-xs uppercase tracking-wide text-textSecondary">{t('onlineBooking.shop.serviceSection')}</p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-amber-800">{t('onlineBooking.service.consultationRecommendation')}</p>
              <p className="text-xs text-amber-800 mt-0.5 line-clamp-2 sm:line-clamp-none">{t('onlineBooking.service.consultationRecommendationDesc')}</p>
            </div>
          </div>

          {loadingServices ? (
            <div className="flex items-center justify-center py-6 text-textSecondary">
              <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              <span className="ml-2 text-sm">{t('onlineBooking.service.loading')}</span>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-6">
              <AlertTriangle className="w-10 h-10 text-orange-500 mx-auto mb-3" />
              <p className="text-gray-600 mb-1 text-sm">{t('onlineBooking.service.noServices')}</p>
              <p className="text-xs text-gray-500">
                {t('onlineBooking.service.contactMessage')}
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {hasConsultation && (
                <div>
                  <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                    <MessageCircle className="w-4 h-4 text-primary-300" />
                    {t('onlineBooking.service.consultationSection')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {consultationServices.map(service => (
                      <div
                        key={service.id}
                        onClick={() => handleServiceSelect(service)}
                        className={selectionCardClass(formData.serviceId === service.id)}
                      >
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-100 text-textPrimary border border-primary-200">
                            {t('onlineBooking.service.consultationBadge')}
                          </span>
                        </div>
                        <h3 className="font-semibold text-textPrimary text-sm sm:text-base leading-snug">{service.name}</h3>
                        {service.description && (
                          <p className="text-xs sm:text-sm text-textSecondary mt-0.5 line-clamp-2">{service.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <p className="text-sm font-medium text-textPrimary">{t('onlineBooking.service.free')}</p>
                          <p className="text-xs text-textSecondary">~{service.estimatedDuration} min</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tattooServices.length > 0 && (
                <div>
                  {hasConsultation && (
                    <h4 className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                      <Palette className="w-4 h-4 text-blue-600" />
                      {t('onlineBooking.service.tattooSection')}
                    </h4>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {tattooServices.map(service => {
                      const depositAmount = calculateServiceDepositAmount(service);
                      return (
                      <div
                        key={service.id}
                        onClick={() => handleServiceSelect(service)}
                        className={selectionCardClass(formData.serviceId === service.id)}
                      >
                        <h3 className="font-semibold text-textPrimary text-sm sm:text-base leading-snug">{service.name}</h3>
                        {service.description && (
                          <p className="text-xs sm:text-sm text-textSecondary mt-0.5 line-clamp-2">{service.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <p className="text-xs text-textSecondary">~{service.estimatedDuration} min</p>
                        </div>
                        {depositAmount > 0 && (
                          <p className="text-xs text-orange-600 mt-0.5">{t('onlineBooking.service.deposit')} €{depositAmount.toFixed(2)}</p>
                        )}
                        <p className="text-xs text-textSecondary mt-1">{t('onlineBooking.service.quoteNote')}</p>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    );
  };
  
  const renderPractitionerSelection = () => (
    <div className="space-y-2.5 sm:space-y-3">
      <h2 className="text-xl sm:text-2xl font-bold leading-tight">{t('onlineBooking.practitioner.title')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {practitioners.map(practitioner => (
          <div
            key={practitioner.id}
            onClick={() => handlePractitionerSelect(practitioner)}
            className={selectionCardClass(formData.practitionerId === practitioner.id)}
          >
            <h3 className="font-semibold text-textPrimary text-sm sm:text-base leading-snug">{getPractitionerDisplayName(practitioner)}</h3>
            {practitioner.bio && (
              <p className="text-xs sm:text-sm text-textSecondary mt-0.5 line-clamp-2 sm:line-clamp-3">{practitioner.bio}</p>
            )}
            <p className="text-xs font-medium mt-1.5 text-textPrimary">{t('onlineBooking.practitioner.rating', { rating: practitioner.rating, count: practitioner.reviewCount })}</p>
          </div>
        ))}
      </div>
    </div>
  );
  
  const renderProjectDetails = () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('client_token') : null;
    const canUploadNow = isAuthenticated || !!token;
    const totalImages = formData.referenceImages.length + pendingImages.length;

    return (
    <div className="space-y-4 sm:space-y-5">
      <h2 className="text-xl sm:text-2xl font-bold leading-tight">{t('onlineBooking.project.title')}</h2>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.description')}</label>
        <textarea
          value={formData.project.description}
          onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, description: e.target.value } }))}
          className="w-full p-3 border rounded-lg"
          rows={4}
          placeholder={t('onlineBooking.project.descriptionPlaceholder')}
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.placement')}</label>
        <input
          type="text"
          value={formData.project.placement.area}
          onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, placement: { ...prev.project.placement, area: e.target.value } } }))}
          className="w-full p-3 border rounded-lg"
          placeholder={t('onlineBooking.project.placementPlaceholder')}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.width')}</label>
          <input
            type="number"
            value={formData.project.size.width || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, size: { ...prev.project.size, width: parseFloat(e.target.value) || 0 } } }))}
            className="w-full p-3 border rounded-lg"
            min="0"
            step="0.1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.height')}</label>
          <input
            type="number"
            value={formData.project.size.height || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, size: { ...prev.project.size, height: parseFloat(e.target.value) || 0 } } }))}
            className="w-full p-3 border rounded-lg"
            min="0"
            step="0.1"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.unit')}</label>
        <select
          value={formData.project.size.unit}
          onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, size: { ...prev.project.size, unit: e.target.value as 'cm' | 'inch' } } }))}
          className="w-full p-3 border rounded-lg"
        >
          <option value="cm">{t('onlineBooking.project.cm')}</option>
          <option value="inch">{t('onlineBooking.project.inch')}</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.style')}</label>
        <select
          value={formData.project.style}
          onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, style: e.target.value as any } }))}
          className="w-full p-3 border rounded-lg"
        >
          <option value="TRADITIONAL">{t('onlineBooking.project.styleOptions.TRADITIONAL')}</option>
          <option value="REALISM">{t('onlineBooking.project.styleOptions.REALISM')}</option>
          <option value="WATERCOLOR">{t('onlineBooking.project.styleOptions.WATERCOLOR')}</option>
          <option value="GEOMETRIC">{t('onlineBooking.project.styleOptions.GEOMETRIC')}</option>
          <option value="MINIMALIST">{t('onlineBooking.project.styleOptions.MINIMALIST')}</option>
          <option value="BLACKWORK">{t('onlineBooking.project.styleOptions.BLACKWORK')}</option>
          <option value="DOTWORK">{t('onlineBooking.project.styleOptions.DOTWORK')}</option>
          <option value="TRIBAL">{t('onlineBooking.project.styleOptions.TRIBAL')}</option>
          <option value="JAPANESE">{t('onlineBooking.project.styleOptions.JAPANESE')}</option>
          <option value="BIOMECHANICAL">{t('onlineBooking.project.styleOptions.BIOMECHANICAL')}</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.colorType')}</label>
        <select
          value={formData.project.colorType}
          onChange={(e) => setFormData(prev => ({ ...prev, project: { ...prev.project, colorType: e.target.value as any } }))}
          className="w-full p-3 border rounded-lg"
        >
          <option value="BLACK_GREY">{t('onlineBooking.project.blackGrey')}</option>
          <option value="COLOR">{t('onlineBooking.project.color')}</option>
          <option value="MIXED">{t('onlineBooking.project.mixed')}</option>
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">{t('onlineBooking.project.notes')}</label>
        <RichTextEditor
          value={formData.project.notesToArtist || ''}
          onChange={(value) =>
            setFormData(prev => ({ ...prev, project: { ...prev.project, notesToArtist: value } }))
          }
          placeholder={t('onlineBooking.project.notesPlaceholder')}
        />
      </div>

      <div className="border-t border-gray-200 pt-3 sm:pt-4 space-y-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera className="w-5 h-5" />
            {t('onlineBooking.images.title')}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{t('onlineBooking.images.description')}</p>
          {!canUploadNow && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
              {t('onlineBooking.images.pendingUntilAuth')}
            </p>
          )}
        </div>

        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageUpload(file);
              e.target.value = '';
            }}
            className="hidden"
            id="image-upload"
            disabled={uploadingImage || totalImages >= 5}
          />
          <label
            htmlFor="image-upload"
            className={`cursor-pointer flex flex-col items-center ${
              uploadingImage || totalImages >= 5
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
          >
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm font-medium">
              {uploadingImage ? t('onlineBooking.images.uploading') : t('onlineBooking.images.clickToUpload')}
            </span>
          </label>
        </div>

        {(formData.referenceImages.length > 0 || pendingImages.length > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            {formData.referenceImages.map(image => (
              <div key={image.imageId} className="relative">
                <img src={image.thumbnailUrl || image.url} alt={image.fileName} className="w-full h-32 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(image.imageId)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {pendingImages.map(image => (
              <div key={image.localId} className="relative">
                <img src={image.previewUrl} alt={image.fileName} className="w-full h-32 object-cover rounded-lg" />
                <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-wide bg-black/60 text-white px-1.5 py-0.5 rounded">
                  {t('onlineBooking.images.pendingBadge')}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemovePendingImage(image.localId)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{t('onlineBooking.images.rightsTitle')}</strong> {t('onlineBooking.images.rightsMessage')}
          </p>
        </div>
      </div>
    </div>
    );
  };
  
  const renderSlotSelection = () => {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 90); // 3 months ahead
    
    const selectedDateSlots = filterPastSlotsForToday(
      availableSlots.filter((slot) => formatLocalDate(new Date(slot.start)) === formData.appointmentDate),
      formData.appointmentDate,
    );
    const availableSlotsForDate = selectedDateSlots.filter(slot => slot.isAvailable);
    const hasAvailableSlots = availableSlotsForDate.length > 0;
    
    const planningRecommendation = hasVisibleHtmlContent(selectedService?.planningRecommendation)
      ? selectedService!.planningRecommendation!
      : null;

    return (
      <div className="space-y-3 sm:space-y-4">
        <h2 className="text-xl sm:text-2xl font-bold leading-tight">{t('onlineBooking.slot.title')}</h2>

        {planningRecommendation && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900 max-w-none">
              <div
                className="[&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                dangerouslySetInnerHTML={{ __html: planningRecommendation }}
              />
            </div>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium mb-2" htmlFor="tattoo-booking-date">
            {t('onlineBooking.slot.date')}
          </label>
          <DateInput
            id="tattoo-booking-date"
            value={formData.appointmentDate}
            onChange={(e) => {
              const newDate = e.target.value;
              setFormData(prev => ({ ...prev, appointmentDate: newDate, selectedTimeSlot: '' }));
              setAvailableSlots([]);
              setSlotsLoaded(false);
              if (formData.shopId && formData.serviceId && newDate) {
                loadAvailability(newDate);
              }
            }}
            min={today.toISOString().split('T')[0]}
            max={maxDate.toISOString().split('T')[0]}
            className="w-full p-3 border rounded-lg text-textPrimary bg-surface min-h-12"
          />
        </div>
        
        {formData.appointmentDate && (
          <div>
            <label className="block text-sm font-medium mb-2">{t('onlineBooking.slot.availableSlots')}</label>
            {loadingSlots ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                <span className="ml-2">{t('onlineBooking.slot.loading')}</span>
              </div>
            ) : (
              <>
                {slotsLoaded && !hasAvailableSlots && (
                  <div className="text-center py-4 mb-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                      <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                      <p className="text-yellow-800 font-medium mb-2">{t('onlineBooking.slot.noSlots')}</p>
                      <p className="text-sm text-yellow-700">{t('onlineBooking.slot.noSlotsHint')}</p>
                    </div>
                  </div>
                )}
                {selectedDateSlots.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {selectedDateSlots.map((slot, index) => {
                      const time24h = new Date(slot.start).toTimeString().slice(0, 5);
                      const timeDisplay = new Date(slot.start).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      });
                      const isSelected = formData.selectedTimeSlot === time24h;
                      return (
                        <button
                          key={`${slot.start}-${index}`}
                          type="button"
                          onClick={() => {
                            if (slot.isAvailable) {
                              handleTimeSlotSelect(time24h, availableSlotsForDate.length);
                            }
                          }}
                          disabled={!slot.isAvailable}
                          className={`px-4 py-2 text-sm rounded-lg border-2 transition-colors ${
                            isSelected
                              ? 'border-primary-500 bg-primary-500 text-white'
                              : slot.isAvailable
                                ? 'border-gray-200 hover:border-primary-300 text-textPrimary'
                                : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {timeDisplay}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };
  
  const renderSummary = () => {
    const depositAmount = isConsultation ? 0 : calculateServiceDepositAmount(selectedService);
    
    return (
      <div className="space-y-4 sm:space-y-5">
        <h2 className="text-xl sm:text-2xl font-bold leading-tight">
          {isConsultation ? t('onlineBooking.summary.consultationTitle') : t('onlineBooking.summary.title')}
        </h2>

        {isConsultation && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-start gap-3">
            <MessageCircle className="w-5 h-5 text-primary-300 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-textSecondary">{t('onlineBooking.summary.consultationInfo')}</p>
          </div>
        )}

        {!isConsultation && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-textSecondary">{t('onlineBooking.summary.quoteNote')}</p>
          </div>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          <div className="flex justify-between">
            <span>{t('onlineBooking.summary.shop')}</span>
            <span className="font-medium">{selectedShop?.name}</span>
          </div>
          <div className="flex justify-between">
            <span>{t('onlineBooking.summary.service')}</span>
            <span className="font-medium">{selectedService?.name}</span>
          </div>
          {selectedPractitioner && (
            <div className="flex justify-between">
              <span>{t('onlineBooking.summary.artist')}</span>
              <span className="font-medium">{getPractitionerDisplayName(selectedPractitioner)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>{t('onlineBooking.summary.dateTime')}</span>
            <span className="font-medium">
              {formData.appointmentDate && new Date(formData.appointmentDate).toLocaleDateString('fr-FR')} {t('onlineBooking.summary.dateTimeAt')} {formData.selectedTimeSlot || ''}
            </span>
          </div>
          {isConsultation ? (
            <div className="flex justify-between">
              <span>{t('onlineBooking.summary.price')}</span>
              <span className="font-medium">{t('onlineBooking.service.free')}</span>
            </div>
          ) : depositAmount > 0 ? (
            <div className="flex justify-between">
              <span>{t('onlineBooking.summary.deposit')}</span>
              <span className="font-medium text-orange-600">€{depositAmount.toFixed(2)}</span>
            </div>
          ) : null}
        </div>
        
        <div className="space-y-2">
         <label className="flex items-start space-x-2">
          <input
            type="checkbox"
            checked={formData.termsAccepted}
            onChange={(e) =>
              setFormData(prev => ({
                ...prev,
                termsAccepted: e.target.checked
              }))
            }
            className="w-4 h-4 mt-1"
          />

          <span className="text-sm text-gray-700">
            {t('onlineBooking.summary.termsAcceptedPrefix')}{' '}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary-600 underline hover:text-primary-700"
              onClick={(e) => e.stopPropagation()}
            >
              {t('onlineBooking.summary.termsAcceptedLink')}
            </a>
            . *
          </span>
        </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.cancellationPolicyAccepted}
              onChange={(e) => setFormData(prev => ({ ...prev, cancellationPolicyAccepted: e.target.checked }))}
              className="w-4 h-4"
            />
            <span>{t('onlineBooking.summary.cancellationAccepted')}</span>
          </label>
          {!isConsultation && (
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.imageRightsAccepted}
                onChange={(e) => setFormData(prev => ({ ...prev, imageRightsAccepted: e.target.checked }))}
                className="w-4 h-4"
              />
              <span>{t('onlineBooking.summary.imageRightsAccepted')}</span>
            </label>
          )}
        </div>
      </div>
    );
  };
  
  const renderPayment = () => {
    if (!bookingResult) return null;
    
    const depositAmount = bookingResult.depositAmount || 0;
    
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold">{t('onlineBooking.payment.title')}</h2>

        {isTestMode && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800">
            <span className="text-lg leading-none">⚠️</span>
            <span>
              <strong>Mode test activé</strong> — ce paiement sera traité via la passerelle active (Stripe ou Viva.com) en mode test.
              Aucun montant réel ne sera débité.
            </span>
          </div>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between mb-4">
            <span>{t('onlineBooking.payment.depositAmount')}</span>
            <span className="font-bold text-lg">€{depositAmount.toFixed(2)}</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">{t('onlineBooking.payment.paymentMethod')}</label>
          <select
            value={formData.paymentMethod}
            onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as OnlineBookingPaymentMethod }))}
            className="w-full p-3 border rounded-lg"
          >
            {ENABLED_ONLINE_BOOKING_PAYMENT_METHOD_OPTIONS.map((method) => (
              <option key={method.value} value={method.value}>
                {t(method.labelKey)}
              </option>
            ))}
          </select>
        </div>
        
        <p className="text-sm text-gray-600">
          {t('onlineBooking.payment.note')}
        </p>
      </div>
    );
  };
  
  const renderSuccess = () => {
    if (!bookingResult) return null;
    const isConsultationResult = bookingResult.appointmentType === 'CONSULTATION';
    const clientName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
    const scheduledDate = formData.appointmentDate
      ? new Date(`${formData.appointmentDate}T${formData.selectedTimeSlot || '00:00'}`)
      : null;

    return (
      <div className="text-center space-y-6">
        <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>

        <h2 className="text-2xl font-display font-bold text-textPrimary">
          {isConsultationResult ? t('onlineBooking.success.consultationTitle') : t('onlineBooking.success.title')}
        </h2>

        {clientName && (
          <p className="text-lg text-textPrimary">
            {t('payment.success.greeting', { name: clientName })}
          </p>
        )}

        <p className="text-textSecondary max-w-lg mx-auto">
          {isConsultationResult ? t('onlineBooking.success.consultationMessage') : t('onlineBooking.success.message')}
        </p>

        <div className="bg-gray-50 rounded-lg p-4 text-left space-y-3 max-w-md mx-auto">
          <div className="text-sm font-medium text-primary-700 uppercase tracking-wide">
            {isConsultationResult
              ? t('payment.success.typeConsultation', 'Consultation tatouage')
              : t('payment.success.typeTattoo', 'Séance de tatouage')}
          </div>

          {scheduledDate && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>
                {scheduledDate.toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}

          {selectedShop?.name && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span>{selectedShop.name}</span>
            </div>
          )}

          {selectedService?.name && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4 flex-shrink-0" />
              <span>{selectedService.name}</span>
            </div>
          )}
        </div>

        {bookingResult.qrCodeImage && (
          <div className="flex flex-col items-center gap-3">
            <img src={bookingResult.qrCodeImage} alt={t('onlineBooking.success.qrCodeAlt')} className="w-48 h-48" />
            {bookingResult.checkInCode && (
              <div className="inline-flex flex-col items-center rounded-xl border border-gray-200 bg-gray-50 px-6 py-3">
                <span className="text-xs uppercase tracking-wider text-gray-500">
                  {t('onlineBooking.success.checkInCodeLabel', "Code d'accueil")}
                </span>
                <span className="mt-1 text-2xl font-bold tracking-[0.35em] text-gray-900">
                  {bookingResult.checkInCode}
                </span>
                <span className="mt-1 text-xs text-gray-500 text-center">
                  {t('onlineBooking.success.checkInCodeHint', 'Donnez ce code à l’accueil si le scan n’est pas possible')}
                </span>
              </div>
            )}
          </div>
        )}

        <p className="text-sm text-gray-500">
          {t('onlineBooking.success.referenceLabel')} {bookingResult.appointmentId}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors inline-flex items-center justify-center gap-2"
          >
            {t('payment.success.backHome', "Retour à l'accueil")}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };
  
  const handleCustomerComplete = async (clientId: string, clientData: ClientData) => {
    // Legacy path: account created without auto-login → redirect to login.
    if (clientData.accountCreated && !clientData.autoLoggedIn && !isAuthenticated) {
      const updatedFormData = { ...formData, clientId, preferredLanguage: clientData.preferredLanguage };
      try {
        const payload = JSON.stringify({ formData: updatedFormData, currentStep: 'customer' as WizardStep });
        sessionStorage.setItem(TATTOO_BOOKING_RESUME_KEY, payload);
        localStorage.setItem(TATTOO_CADDY_STORAGE_KEY, payload);
      } catch { /* ignore */ }

      const returnUrl = `${window.location.pathname}?step=customer`;
      navigate('/login', {
        state: {
          from: { pathname: returnUrl },
          accountJustCreated: true,
          bookingUserName: `${clientData.firstName} ${clientData.lastName}`.trim(),
        },
        replace: true,
      });
      return;
    }

    setFormData(prev => {
      if (prev.clientId === clientId && prev.preferredLanguage === clientData.preferredLanguage) {
        return prev;
      }
      return {
        ...prev,
        clientId,
        preferredLanguage: clientData.preferredLanguage
      };
    });

    // Upload any deferred reference images now that the client is authenticated
    if (pendingImagesRef.current.length > 0) {
      try {
        await flushPendingImages();
      } catch (err: any) {
        setError(err.message || t('onlineBooking.images.uploadFailed'));
        return;
      }
    }

    goToNextStep();
  };

  const renderCustomerStep = () => {
    // Simple flow:
    // - Authenticated: CustomerDetailsStep shows confirmation (book for me / log out)
    // - Not authenticated: CustomerDetailsStep shows login / create account options
    const customerInitialData = isAuthenticated && user ? {
      id: (user as any).id,
      firstName: (user as any).firstName || '',
      lastName: (user as any).lastName || '',
      email: (user as any).email || '',
      phone: (user as any).phone || '',
      dateOfBirth: user.dateOfBirth || '',
      preferredLanguage: (user as any).preferredLanguage || formData.preferredLanguage,
    } : undefined;

    return (
      <CustomerDetailsStep
        onComplete={handleCustomerComplete}
        onBack={handleBack}
        onLogout={() => {
          // Reset the entire booking process so the new user starts fresh
          setFormData(getDefaultFormData());
        }}
        bookingType="tattoo"
        requireLegalGuardian={true}
        preferredLanguage={formData.preferredLanguage}
        initialData={customerInitialData}
        hideNavigation={false}
      />
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'shop':
        return renderShopSelection();
      case 'practitioner':
        return renderPractitionerSelection();
      case 'project':
        return renderProjectDetails();
      case 'customer':
        return renderCustomerStep();
      case 'slot':
        return renderSlotSelection();
      case 'summary':
        return renderSummary();
      case 'payment':
        return renderPayment();
      case 'success':
        return renderSuccess();
      default:
        return null;
    }
  };
  
  if (loading && currentStep === 'shop') {
    return (
      <div ref={bookingTopRef} className="container mx-auto px-4 pt-24 pb-8">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div ref={bookingTopRef} className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-20 sm:pt-24 pb-24 md:pb-8">
      {/* Progress Steps */}
      {currentStep !== 'payment' && currentStep !== 'success' && (
        <>
          <BookingStepper
            variant="icons"
            steps={steps}
            currentIndex={Math.max(0, currentStepIndex)}
          />
          <LandscapeHint active={currentStep === 'slot'} />
        </>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}
      
      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-md p-3 sm:p-5 mb-4 sm:mb-6">
        {currentStep !== 'payment' && currentStep !== 'success' && currentStep !== 'summary' && (
          <BookingWizardContext items={bookingContextItems} />
        )}
        {renderStep()}
      </div>
      
      {/* Navigation Buttons */}
      {currentStep !== 'payment' && currentStep !== 'success' && (
        <div className="sticky bottom-0 z-20 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 py-2.5 mt-1 border-t border-gray-200 bg-background/95 backdrop-blur flex items-center justify-between gap-3 md:static md:z-auto md:mx-0 md:mt-6 md:px-0 md:py-0 md:border-0 md:bg-transparent md:backdrop-blur-none">
          <button
            onClick={handleBack}
            disabled={currentStepIndex === 0}
            className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('common.previous')}</span>
          </button>
          
          {currentStep === 'summary' ? (
            <button
              onClick={handleCreateBooking}
              disabled={!canProceed() || loading}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('onlineBooking.buttons.creatingBooking')}</span>
                </>
              ) : (
                <>
                  <span>{t('onlineBooking.buttons.confirmBooking')}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : currentStep === 'customer' ? (
            // Customer step handles its own navigation via CustomerDetailsStep
            <div />
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{t('common.next')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      
      {currentStep === 'payment' && (
        <div className="flex justify-center">
          <button
            onClick={handleDepositPayment}
            disabled={loading}
            className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('onlineBooking.buttons.processingPayment')}</span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                <span>{t('onlineBooking.buttons.payDeposit')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

